import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Form } from 'react-final-form';
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  Typography,
  useTheme,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { flow } from 'lodash';
import CloseIcon from '@mui/icons-material/Close';

import { FIELDS } from '../../../constants/forms';
import {
  FOUND_REFERENCE_NUMBER,
  TRACK,
  TRACK_YOUR_PARCEL,
} from '../../../constants/strings';
import {
  trackParcelSchema,
  validateWithJoi,
} from '../../../helpers/validators';
import { replaceLetters } from '../../../utils/strings';
import { StringUtil } from '../../../utils';
import useGetParcelParams from '../../../hooks/useGetParcelParams';
import { useOverlay } from '../../../features/Overlay';
import parcelSlice from '../../../redux/parcelSlice';
import { FormHelpers, ParcelHelpers } from '../../../helpers';
import { FormInput } from '../../../components/FormInput';
import { ROUTE_PATH } from '../../../constants';
import useBreakpoint from '../../../hooks/useBreakpoint';
import InitialSection from './InitialSection';

const Sidebar = ({ setDrawerOpen }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const overlay = useOverlay();
  const dispatch = useDispatch();
  const isSmallScreen = useBreakpoint('md');

  const [disabledSearch, setIsDisabledSearch] = useState(false);
  const { parcelNumber, postcodeOrOrderId } = useGetParcelParams();

  const initialValues = useMemo(
    () => ({
      [FIELDS.PARCEL_NUMBER.KEY]: parcelNumber,
      [FIELDS.POSTCODE_OR_ORDER_ID_SEARCH.KEY]: postcodeOrOrderId,
    }),
    [parcelNumber, postcodeOrOrderId]
  );

  const resetField = useCallback((form, field) => {
    form.change(field, '');
    form.resetFieldState(field);
  }, []);

  const handleFormSubmit = useCallback(
    async formValues => {
      const { parcelNumber, postcodeOrOrderId } = formValues;

      if (parcelNumber) {
        try {
          overlay.show();

          const { parcelCode } = await dispatch(
            parcelSlice.actions.fetchParcelCodeByParcelNumber({
              parcelNumber,
              postcodeOrOrderId,
            })
          ).unwrap();

          for (let key in localStorage) {
            if (key.startsWith('tracking/')) {
              localStorage.removeItem(key);
            }
          }

          localStorage.setItem(`tracking/${parcelCode}`, postcodeOrOrderId);

          if (parcelCode) {
            navigate(`${ROUTE_PATH.TRACK}/${parcelCode}`);
          }
        } catch (error) {
          const { message, statusCode } = error;

          navigate(ROUTE_PATH.TRACK, {
            state: {
              error: { message, statusCode },
            },
          });
        } finally {
          overlay.hide();
          setDrawerOpen(true);
        }
      }
    },
    [dispatch, navigate, overlay]
  );

  return (
    <Box>
      <Drawer
        anchor='left'
        variant='permanent'
        sx={{
          height: '100%',
        }}
        PaperProps={{
          sx: {
            width: { xs: '100%', md: 300, lg: 330 },
            borderRight: {
              xs: 'none',
              md: `1px solid ${theme.vars.palette.primary.borderGray}`,
            },
            position: 'static',
            px: { xs: 2, md: 4 },
            height: {
              xs: 'auto',
              md: '100%',
            },
          },
        }}
      >
        <List
          sx={{
            display: { xs: 'flex' },
            flexDirection: 'column',
            height: {
              md: '100%',
            },
            mt: 1,
          }}
        >
          <ListItem>
            <Typography variant='h3' component='h1' sx={{ pt: 2, pb: 2 }}>
              {TRACK_YOUR_PARCEL}
            </Typography>
          </ListItem>
          <ListItem>
            <Form
              onSubmit={handleFormSubmit}
              validate={values => validateWithJoi(values, trackParcelSchema)}
              initialValues={initialValues}
              render={({ handleSubmit, form, values, invalid }) => (
                <form onSubmit={handleSubmit}>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    <FormInput
                      name={FIELDS.PARCEL_NUMBER.KEY}
                      label={FIELDS.PARCEL_NUMBER.LABEL}
                      placeholder={FIELDS.PARCEL_NUMBER.PLACEHOLDER}
                      required
                      fullWidth
                      onChange={() => {
                        setIsDisabledSearch(false);
                      }}
                      InputProps={{
                        inputmode: 'numeric',
                        autoComplete: 'off',
                        pattern: '[0-9]*',
                        endAdornment: !!values[FIELDS.PARCEL_NUMBER.KEY]
                          ?.length && (
                          <InputAdornment position='end'>
                            <IconButton
                              onClick={() =>
                                resetField(form, FIELDS.PARCEL_NUMBER.KEY)
                              }
                              edge='end'
                              aria-label='delete'
                            >
                              <CloseIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      fieldProps={{
                        parse: flow([
                          replaceLetters,
                          ParcelHelpers.normalizeParcelNumber,
                        ]),
                      }}
                    />
                    <FormInput
                      fullWidth
                      name={FIELDS.POSTCODE_OR_ORDER_ID_SEARCH.KEY}
                      label={FIELDS.POSTCODE_OR_ORDER_ID_SEARCH.LABEL}
                      placeholder={
                        FIELDS.POSTCODE_OR_ORDER_ID_SEARCH.PLACEHOLDER
                      }
                      onChange={() => {
                        setIsDisabledSearch(false);
                      }}
                      InputProps={{
                        autoComplete: 'off',
                        endAdornment: !!values[
                          FIELDS.POSTCODE_OR_ORDER_ID_SEARCH.KEY
                        ]?.length && (
                          <InputAdornment position='end'>
                            <IconButton
                              onClick={() =>
                                resetField(
                                  form,
                                  FIELDS.POSTCODE_OR_ORDER_ID_SEARCH.KEY
                                )
                              }
                              edge='end'
                              aria-label='delete'
                            >
                              <CloseIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      fieldProps={{
                        ...FormHelpers.fieldTrimOnBlur,
                        parse: StringUtil.trimFirstAndLastSpaces,
                      }}
                      maxLength={20}
                      required
                    />
                    <Button
                      variant='contained'
                      type='submit'
                      size='large'
                      disabled={invalid || disabledSearch}
                    >
                      {TRACK}
                    </Button>
                    <Divider />
                    <Typography variant='body1' sx={{ mb: { xs: 4, md: 0 } }}>
                      {FOUND_REFERENCE_NUMBER}
                    </Typography>
                  </Box>
                </form>
              )}
            />
          </ListItem>
        </List>
        {isSmallScreen && <InitialSection />}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
